import moment from "moment";
import { CalendarEventType, TimeBlockEvent } from "../../../types/calendar";
import { DEFAULT_TIMEZONE } from "../../../utils/timeUtils";

/** Month buffer around a date: 1 month before, current, 2 months after. */
export function getMonthBufferDates(date: Date) {
  const targetMonth = moment(date);
  const startMonth = targetMonth.clone().subtract(1, "month");
  const endMonth = targetMonth.clone().add(2, "months");

  return {
    start: startMonth.startOf("month").format("YYYY-MM-DD"),
    end: endMonth.endOf("month").format("YYYY-MM-DD"),
    months: [
      startMonth.format("YYYY-MM"),
      targetMonth.format("YYYY-MM"),
      targetMonth.clone().add(1, "month").format("YYYY-MM"),
      endMonth.format("YYYY-MM"),
    ],
  };
}

/** Map API time-off rows to calendar time-block events. */
export function mapTimeBlocksToEvent(
  timeBlocks: any[],
  timezone: string | undefined,
  staffId?: string,
): TimeBlockEvent[] {
  const tz = timezone || DEFAULT_TIMEZONE;
  return timeBlocks.map((block) => ({
    id: block.id,
    title: `[Block] ${block.type}${block.description ? ` - ${block.description}` : ""}`,
    // Build the Date so its browser-local wall-clock equals the salon's
    // wall-clock (react-big-calendar renders Dates in browser-local time, and
    // momentLocalizer has no salon-tz default). This mirrors how appointments
    // are mapped in transactionUtils.mapBookedServiceToEvent; using .toDate()
    // here would instead show the raw instant in the browser's timezone.
    start: new Date(
      moment.utc(block.start_dt).tz(tz).format("YYYY-MM-DD HH:mm"),
    ),
    end: new Date(moment.utc(block.end_dt).tz(tz).format("YYYY-MM-DD HH:mm")),
    resourceId: staffId || block.staff_id,
    isTimeBlock: true as const,
    type: block.type,
    description: block.description,
    recurrenceParentId: block.recurrence_parent_id,
    recurrenceInterval: block.recurrence_interval,
    recurrenceUnit: block.recurrence_unit,
    recurrenceEndDate: block.recurrence_end_dt,
    status: block.status,
  }));
}

/** Shallow-patch one event by id. */
export function replaceEventInList(
  events: CalendarEventType[],
  eventId: string,
  next: Partial<CalendarEventType>,
): CalendarEventType[] {
  return events.map((e) =>
    e.id === eventId ? ({ ...e, ...next } as CalendarEventType) : e,
  );
}

/**
 * Merge edited time blocks into the list by id, keeping fields the edit
 * response left out. A partial response maps those fields to `undefined`
 * (e.g. the recurrence fields), and replacing the event outright would detach
 * it from its series, so the next edit wouldn't offer "This / Following /
 * All". Ids not already in the list are appended.
 */
export function mergeUpdatedTimeBlocks(
  events: CalendarEventType[],
  updated: TimeBlockEvent[],
): CalendarEventType[] {
  const byId = new Map(events.map((event) => [event.id, event]));
  updated.forEach((block) => {
    const defined = Object.fromEntries(
      Object.entries(block).filter(([, value]) => value !== undefined),
    );
    byId.set(block.id, { ...byId.get(block.id), ...defined } as TimeBlockEvent);
  });
  return Array.from(byId.values());
}

/** Drop a transaction's events and append its freshly-derived ones. */
export function replaceTransactionEvents(
  events: CalendarEventType[],
  transactionId: string,
  nextEvents: CalendarEventType[],
): CalendarEventType[] {
  const others = events.filter(
    (event) =>
      !("transactionId" in event) || event.transactionId !== transactionId,
  );
  return [...others, ...nextEvents];
}

/**
 * Merge a range fetch into existing events. An event that used to be in local
 * state but that the server did not return for the fetched range has been
 * deleted (or moved out) server-side, so evict it — otherwise cross-device
 * deletions never propagate without a full page reload. Events whose interval
 * does not overlap the fetched range are outside what we asked about and are
 * kept untouched.
 */
export function mergeFetchedEvents(
  prevEvents: CalendarEventType[],
  newEvents: CalendarEventType[],
  range: { startDate: string; endDate: string },
  succeeded: { transactions: boolean; timeBlocks: boolean },
): CalendarEventType[] {
  const getEventKey = (event: CalendarEventType): string =>
    event.isTimeBlock ? `timeblock-${event.id}` : `appointment-${event.id}`;

  // Both event.start/.end and the range strings are salon-wall-clock
  // interpreted in browser-local time (see mapTimeBlocksToEvent and
  // getMonthBufferDates), so comparing them as epoch ms is correct.
  const rangeStartMs = new Date(`${range.startDate}T00:00:00`).getTime();
  const rangeEndMs = new Date(`${range.endDate}T23:59:59.999`).getTime();
  const newEventKeys = new Set(newEvents.map(getEventKey));

  const eventMap = new Map<string, CalendarEventType>();
  prevEvents.forEach((event) => {
    const key = getEventKey(event);
    const startMs = event.start?.getTime?.() ?? 0;
    const endMs = event.end?.getTime?.() ?? startMs;
    const overlapsFetched = endMs >= rangeStartMs && startMs <= rangeEndMs;
    // Only evict events of a type whose request actually succeeded this
    // round — Promise.allSettled means a transient failure (e.g. a 403/5xx on
    // just the timeoff or transactions call) must not wipe out
    // previously-loaded events of that type.
    const fetchSucceededForType = event.isTimeBlock
      ? succeeded.timeBlocks
      : succeeded.transactions;
    if (!overlapsFetched || !fetchSucceededForType || newEventKeys.has(key)) {
      eventMap.set(key, event);
    }
  });

  newEvents.forEach((event) => {
    eventMap.set(getEventKey(event), event);
  });

  return Array.from(eventMap.values());
}
